import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Header from '../../components/common/global-top-bar';
import Spinner from '../../components/common/spinner';
import { useSettings, useUpdateSettings } from '../../hooks/useSettings';
import {
  FiScriptLineRow,
  useCreateFiScriptLine,
  useDeleteFiScriptLine,
  useFiScriptLinesList,
  useFiScriptLinesMeta,
  useUpdateFiScriptLine,
} from '../../hooks/useFiScriptLinesAdmin';
import { TextBlocksHeaderOptions, createDefaultValues } from './common/text-blocks-constants';
import SwitchBar from '../../components/switch-bar';

const FI_PROMPT_KEYS = [
  'FI_SYSTEM_PROMPT',
  'FI_INTENT_LISTENERS_PATTERNS',
  'FI_INTENT_RESOURCES_PATTERNS',
  'FI_INTENT_STORIES_PATTERNS',
] as const;

const INTENT_KEYS = [
  'FI_INTENT_LISTENERS_PATTERNS',
  'FI_INTENT_RESOURCES_PATTERNS',
  'FI_INTENT_STORIES_PATTERNS',
] as const;

function jsonArrayStringToCommaList(raw: unknown): string {
  const text = String(raw ?? '').trim();
  if (!text) return '';
  try {
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) return text;
    const items = parsed
      .filter((v) => typeof v === 'string')
      .map((v) => v.trim())
      .filter(Boolean);
    return items.join(', ');
  } catch {
    return text;
  }
}

function commaListToJsonArrayString(raw: string): string {
  const text = String(raw ?? '').trim();
  if (!text) return '';
  const items = text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return JSON.stringify(items);
}

export const FiConcierge = () => {
  const { data: appSettings, isLoading: settingsLoading } = useSettings('email');
  const updateSettings = useUpdateSettings();
  const { data: meta, isLoading: metaLoading } = useFiScriptLinesMeta();
  const { data: lines, isLoading: linesLoading } = useFiScriptLinesList();
  const createLine = useCreateFiScriptLine();
  const updateLine = useUpdateFiScriptLine();
  const deleteLine = useDeleteFiScriptLine();

  const defaultPrompts = useMemo(() => {
    const base = createDefaultValues(FI_PROMPT_KEYS, appSettings) as Record<(typeof FI_PROMPT_KEYS)[number], string>;
    for (const k of INTENT_KEYS) {
      base[k] = jsonArrayStringToCommaList(base[k]);
    }
    return base;
  }, [appSettings]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid },
  } = useForm({
    defaultValues: defaultPrompts,
  });

  const cancelPromptRef = useRef() as React.MutableRefObject<HTMLButtonElement>;

  useEffect(() => {
    if (defaultPrompts) {
      reset(defaultPrompts);
    }
  }, [defaultPrompts, reset]);

  const onSubmitPrompt = (data: Record<string, string>) => {
    const transformed = { ...data };
    for (const k of INTENT_KEYS) {
      transformed[k] = commaListToJsonArrayString(transformed[k]);
    }
    const payload = {
      data: {
        message: appSettings?.message,
        system: { ...appSettings?.system, ...transformed },
      },
    };

    updateSettings.mutate(payload, {
      onSuccess: () => {
        toast.success('Fi system prompt saved.');
        cancelPromptRef.current.classList.remove('bg-red');
        cancelPromptRef.current.classList.add('bg-gray-background-dark');
      },
      onError: () => {
        toast.error('Failed to save settings.');
      },
    });
  };

  const handlePromptCancel = () => {
    reset(defaultPrompts);
    cancelPromptRef.current.classList.remove('bg-red');
    cancelPromptRef.current.classList.add('bg-gray-background-dark');
  };

  const handlePromptChange = (key: (typeof FI_PROMPT_KEYS)[number]) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(key, e.target.value);
    cancelPromptRef.current.classList.remove('bg-gray-background-dark');
    cancelPromptRef.current.classList.add('bg-red');
  };

  const toolKeyMap = useMemo(() => meta?.tool_key_map ?? {}, [meta?.tool_key_map]);

  const sortedTools = useMemo(
    () => Object.keys(toolKeyMap).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [toolKeyMap],
  );

  const sortedResponseKeys = useMemo(() => {
    const fromMeta = meta?.response_keys ?? [];
    const fromLines = [...new Set((lines || []).map((l) => l.response_key))];
    return Array.from(new Set([...fromMeta, ...fromLines])).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
  }, [meta?.response_keys, lines]);

  const linesByKey = useMemo(() => {
    const map = new Map<string, FiScriptLineRow[]>();
    for (const row of lines || []) {
      const list = map.get(row.response_key) ?? [];
      list.push(row);
      map.set(row.response_key, list);
    }
    for (const [, list] of map) {
      list.sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
    }
    return map;
  }, [lines]);

  const [selectedTool, setSelectedTool] = useState<string>('');
  const [selectedKey, setSelectedKey] = useState<string>('');

  const keysForSelectedTool = useMemo(() => {
    if (!selectedTool) return [];
    return (toolKeyMap[selectedTool] ?? []).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [selectedTool, toolKeyMap]);

  useEffect(() => {
    if (sortedTools.length === 0) return;
    setSelectedTool((prev) => (prev && sortedTools.includes(prev) ? prev : sortedTools[0]));
  }, [sortedTools]);

  useEffect(() => {
    if (keysForSelectedTool.length === 0) {
      setSelectedKey('');
      return;
    }
    setSelectedKey((prev) => (prev && keysForSelectedTool.includes(prev) ? prev : keysForSelectedTool[0]));
  }, [keysForSelectedTool]);

  const [newLine, setNewLine] = useState({
    body: '',
    sort_order: 0,
    is_active: true,
  });

  useEffect(() => {
    if (!selectedKey) return;
    setNewLine((s) => ({ ...s, body: '' }));
  }, [selectedKey]);

  useEffect(() => {
    if (!selectedKey) return;
    const existing = linesByKey.get(selectedKey) ?? [];
    const maxOrder = existing.length ? Math.max(...existing.map((r) => r.sort_order)) : -1;
    setNewLine((s) => ({ ...s, sort_order: maxOrder + 1 }));
  }, [selectedKey, linesByKey]);

  const onAddLine = () => {
    const key = selectedKey.trim();
    const body = newLine.body.trim();
    if (!key || !body) {
      toast.error('Choose a response key and enter body text.');
      return;
    }
    createLine.mutate(
      {
        response_key: key,
        body,
        sort_order: Number(newLine.sort_order) || 0,
        is_active: newLine.is_active,
      },
      {
        onSuccess: () => {
          toast.success('Script line added.');
          setNewLine((s) => ({ ...s, body: '' }));
        },
        onError: () => toast.error('Could not add script line.'),
      },
    );
  };

  const loading = settingsLoading || metaLoading || linesLoading;
  const rowsForSelectedKey = selectedKey ? linesByKey.get(selectedKey) ?? [] : [];

  return (
    <div>
      <Header heading={'Fi concierge (MWA)'} />
      <SwitchBar heading={TextBlocksHeaderOptions} position={5} />
      <div className="max-window-height overflow-y-auto pb-10">
        <div className="w-full px-7">
          {loading && <Spinner />}
          {!loading && (
            <>
              <div className="mt-6">
                <form onSubmit={handleSubmit(onSubmitPrompt)}>
                  <div className="mb-10">
                    <h2 className="mb-4 border-b border-gray-200 pb-2 text-xl font-semibold text-gray-800">
                      System prompt
                    </h2>
                    <p className="mb-6 text-sm text-gray-600">
                      Instructions for Fi (OpenAI tool calling). Required at runtime; stored in app settings as{' '}
                      <code className="text-xs">FI_SYSTEM_PROMPT</code>.
                    </p>
                    <label htmlFor="FI_SYSTEM_PROMPT" className="mb-2 block text-sm font-medium text-gray-700">
                      Fi system prompt
                    </label>
                    <textarea
                      id="FI_SYSTEM_PROMPT"
                      {...register('FI_SYSTEM_PROMPT')}
                      onChange={handlePromptChange('FI_SYSTEM_PROMPT')}
                      className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-64 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                      placeholder="Fi system instructions..."
                      data-testid="fi-system-prompt-textarea"
                    />
                    <div className="mt-4 flex justify-end space-x-4">
                      <button
                        type="submit"
                        disabled={!isValid || updateSettings.isLoading}
                        className="bg-bright-blue rounded-full px-6 py-2 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                      >
                        {updateSettings.isLoading ? 'Saving...' : 'Save prompt'}
                      </button>
                      <button
                        type="button"
                        ref={cancelPromptRef}
                        onClick={handlePromptCancel}
                        className="bg-gray-background-dark rounded-full px-6 py-2 font-medium text-white transition-colors hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>

              <div className="mb-6">
                <h2 className="mb-4 border-b border-gray-200 pb-2 text-xl font-semibold text-gray-800">Script lines</h2>
                <p className="mb-4 text-sm text-gray-600">
                  User-visible copy is chosen at random from active rows per{' '}
                  <code className="text-xs">response_key</code>. If no active DB rows exist for a key, the app uses
                  built-in fallbacks. Pick a key below to view or edit lines for that bucket.
                </p>

                {sortedTools.length === 0 ? (
                  <p className="text-sm text-gray-500">Loading keys…</p>
                ) : (
                  <>
                    <div className="mb-4 flex flex-col gap-3">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-4">
                        <div className="min-w-0 flex-1">
                          <label htmlFor="fi-tool-select" className="mb-1 block text-sm font-medium text-gray-700">
                            Tool
                          </label>
                          <select
                            id="fi-tool-select"
                            className="focus:ring-bright-blue focus:border-bright-blue w-full max-w-xl rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none"
                            value={selectedTool}
                            onChange={(e) => setSelectedTool(e.target.value)}
                          >
                            {sortedTools.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="min-w-0 flex-1">
                          <label
                            htmlFor="fi-script-key-select"
                            className="mb-1 block text-sm font-medium text-gray-700"
                          >
                            Response key
                          </label>
                          <select
                            id="fi-script-key-select"
                            className="focus:ring-bright-blue focus:border-bright-blue w-full max-w-xl rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none"
                            value={selectedKey}
                            onChange={(e) => setSelectedKey(e.target.value)}
                            disabled={keysForSelectedTool.length === 0}
                          >
                            {keysForSelectedTool.map((k) => {
                              const n = linesByKey.get(k)?.length ?? 0;
                              return (
                                <option key={k} value={k}>
                                  {k} ({n} custom {n === 1 ? 'line' : 'lines'})
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        {rowsForSelectedKey.length === 0
                          ? 'No DB rows for this key — app uses code fallbacks until you add lines.'
                          : `${rowsForSelectedKey.length} row(s) in the database for this key.`}
                      </p>
                    </div>

                    <div className="mb-6 rounded-md border border-gray-200 bg-gray-50 p-4">
                      <h3 className="mb-1 text-sm font-semibold text-gray-800">
                        Add line under <code className="rounded bg-white px-1 text-xs">{selectedKey}</code>
                      </h3>
                      <p className="mb-3 text-xs text-gray-500">
                        Sort order defaults to the next available slot; lower numbers are evaluated first when
                        randomizing.
                      </p>
                      <div className="grid gap-3 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">Sort order</label>
                          <input
                            type="number"
                            className="focus:ring-bright-blue w-full rounded border border-gray-300 px-2 py-1 text-sm"
                            value={newLine.sort_order}
                            onChange={(e) =>
                              setNewLine((s) => ({ ...s, sort_order: parseInt(e.target.value, 10) || 0 }))
                            }
                          />
                        </div>
                        <div className="flex items-end pb-1">
                          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                            <input
                              id="new-active"
                              type="checkbox"
                              checked={newLine.is_active}
                              onChange={(e) => setNewLine((s) => ({ ...s, is_active: e.target.checked }))}
                            />
                            Active
                          </label>
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="mb-1 block text-xs font-medium text-gray-600">Body</label>
                        <textarea
                          className="focus:ring-bright-blue h-24 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                          value={newLine.body}
                          onChange={(e) => setNewLine((s) => ({ ...s, body: e.target.value }))}
                          placeholder="Text shown to members..."
                        />
                      </div>
                      <button
                        type="button"
                        onClick={onAddLine}
                        disabled={createLine.isLoading || !selectedKey}
                        className="bg-bright-blue mt-3 rounded-full px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400"
                      >
                        {createLine.isLoading ? 'Adding…' : 'Add script line'}
                      </button>
                    </div>

                    <div className="overflow-x-auto rounded-md border border-gray-200">
                      <div className="border-b border-gray-200 bg-gray-50 px-3 py-2">
                        <span className="text-sm font-medium text-gray-800">Lines for </span>
                        <code className="text-sm">{selectedKey}</code>
                      </div>
                      <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 font-medium text-gray-700">Body</th>
                            <th className="px-3 py-2 font-medium text-gray-700">Order</th>
                            <th className="px-3 py-2 font-medium text-gray-700">Active</th>
                            <th className="px-3 py-2 font-medium text-gray-700">Move key</th>
                            <th className="px-3 py-2 font-medium text-gray-700" />
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {rowsForSelectedKey.map((row) => (
                            <ScriptLineRow
                              key={row.id}
                              row={row}
                              keyOptions={sortedResponseKeys}
                              onSave={(payload) => {
                                updateLine.mutate(
                                  { id: row.id, ...payload },
                                  {
                                    onSuccess: () => toast.success('Saved.'),
                                    onError: () => toast.error('Save failed.'),
                                  },
                                );
                              }}
                              onDelete={() => {
                                if (!window.confirm('Delete this script line?')) return;
                                deleteLine.mutate(row.id, {
                                  onSuccess: () => toast.success('Deleted.'),
                                  onError: () => toast.error('Delete failed.'),
                                });
                              }}
                              busy={updateLine.isLoading || deleteLine.isLoading}
                            />
                          ))}
                        </tbody>
                      </table>
                      {rowsForSelectedKey.length === 0 && (
                        <p className="p-4 text-sm text-gray-500">
                          No custom lines for this key yet. Add one above or rely on built-in fallbacks.
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="mb-10">
                <form onSubmit={handleSubmit(onSubmitPrompt)}>
                  <h2 className="mb-4 border-b border-gray-200 pb-2 text-xl font-semibold text-gray-800">
                    Intent router patterns
                  </h2>
                  <p className="mb-6 text-sm text-gray-600">
                    Optional comma-separated substrings used to route user requests to listeners/resources/stories when
                    no pathway is selected. Leave blank to use app defaults.
                  </p>

                  <div className="grid gap-6 lg:grid-cols-3">
                    <div>
                      <label
                        htmlFor="FI_INTENT_LISTENERS_PATTERNS"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Listeners patterns
                      </label>
                      <textarea
                        id="FI_INTENT_LISTENERS_PATTERNS"
                        {...register('FI_INTENT_LISTENERS_PATTERNS')}
                        onChange={handlePromptChange('FI_INTENT_LISTENERS_PATTERNS')}
                        className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-48 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                        placeholder="talk to someone, connect with a listener"
                      />
                      <div className="mt-2 text-xs text-gray-500">
                        <code className="text-xs">FI_INTENT_LISTENERS_PATTERNS</code>
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="FI_INTENT_RESOURCES_PATTERNS"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Resources patterns
                      </label>
                      <textarea
                        id="FI_INTENT_RESOURCES_PATTERNS"
                        {...register('FI_INTENT_RESOURCES_PATTERNS')}
                        onChange={handlePromptChange('FI_INTENT_RESOURCES_PATTERNS')}
                        className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-48 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                        placeholder="resources, articles, read about"
                      />
                      <div className="mt-2 text-xs text-gray-500">
                        <code className="text-xs">FI_INTENT_RESOURCES_PATTERNS</code>
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="FI_INTENT_STORIES_PATTERNS"
                        className="mb-2 block text-sm font-medium text-gray-700"
                      >
                        Stories patterns
                      </label>
                      <textarea
                        id="FI_INTENT_STORIES_PATTERNS"
                        {...register('FI_INTENT_STORIES_PATTERNS')}
                        onChange={handlePromptChange('FI_INTENT_STORIES_PATTERNS')}
                        className="focus:ring-bright-blue focus:border-bright-blue resize-vertical h-48 w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
                        placeholder="stories, hear stories, experiences"
                      />
                      <div className="mt-2 text-xs text-gray-500">
                        <code className="text-xs">FI_INTENT_STORIES_PATTERNS</code>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-end space-x-4">
                    <button
                      type="submit"
                      disabled={!isValid || updateSettings.isLoading}
                      className="bg-bright-blue rounded-full px-6 py-2 font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-400"
                    >
                      {updateSettings.isLoading ? 'Saving...' : 'Save patterns'}
                    </button>
                    <button
                      type="button"
                      onClick={handlePromptCancel}
                      className="bg-gray-background-dark rounded-full px-6 py-2 font-medium text-white transition-colors hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

function ScriptLineRow({
  row,
  keyOptions,
  onSave,
  onDelete,
  busy,
}: {
  row: FiScriptLineRow;
  keyOptions: string[];
  onSave: (p: Partial<Pick<FiScriptLineRow, 'response_key' | 'body' | 'sort_order' | 'is_active'>>) => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const [response_key, setKey] = useState(row.response_key);
  const [body, setBody] = useState(row.body);
  const [sort_order, setOrder] = useState(row.sort_order);
  const [is_active, setActive] = useState(row.is_active);

  const moveKeyChoices = useMemo(() => {
    const set = new Set(keyOptions);
    if (!set.has(row.response_key)) {
      return [row.response_key, ...keyOptions].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }
    return keyOptions;
  }, [keyOptions, row.response_key]);

  useEffect(() => {
    setKey(row.response_key);
    setBody(row.body);
    setOrder(row.sort_order);
    setActive(row.is_active);
  }, [row]);

  const dirty =
    response_key !== row.response_key ||
    body !== row.body ||
    sort_order !== row.sort_order ||
    is_active !== row.is_active;

  return (
    <tr>
      <td className="min-w-[16rem] px-3 py-2 align-top">
        <textarea
          className="focus:ring-bright-blue w-full rounded border border-gray-200 px-2 py-1 text-sm"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </td>
      <td className="px-3 py-2 align-top">
        <input
          type="number"
          className="w-16 rounded border border-gray-200 px-1 py-0.5 text-sm"
          value={sort_order}
          onChange={(e) => setOrder(parseInt(e.target.value, 10) || 0)}
        />
      </td>
      <td className="px-3 py-2 align-top">
        <input type="checkbox" checked={is_active} onChange={(e) => setActive(e.target.checked)} />
      </td>
      <td className="max-w-[14rem] px-3 py-2 align-top">
        <select
          className="focus:ring-bright-blue w-full rounded border border-gray-200 px-1 py-1 text-xs"
          value={response_key}
          onChange={(e) => setKey(e.target.value)}
        >
          {moveKeyChoices.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
      </td>
      <td className="whitespace-nowrap px-3 py-2 align-top">
        <button
          type="button"
          disabled={!dirty || busy}
          onClick={() =>
            onSave({
              response_key,
              body,
              sort_order,
              is_active,
            })
          }
          className="text-bright-blue mr-2 text-sm font-medium disabled:text-gray-400"
        >
          Save
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onDelete}
          className="text-sm font-medium text-red-600 disabled:text-gray-400"
        >
          Delete
        </button>
      </td>
    </tr>
  );
}
